/**
 * RegistrationFormSection - Registration form fields for developer/demo programs.
 * Extracted from DeveloperRegistrationPageUpdate for file size compliance.
 */

import { AlertCircle, Check, Loader2, X } from 'lucide-react';
import React from 'react';
import { type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { PROGRAM_TYPE, type ProgramType } from '@/application/queries/developerRegistration';
import { ECGridColors } from '@/types/colors';
import type { AccessCodeValidationResult } from './DeveloperRegistrationForm.store';
import type { RegistrationFormValues } from './developer-registration.schema';

// ── Sub-components ─────────────────────────────────────────────────────────

const LoadingSpinner: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <Loader2 className={`animate-spin ${className}`} aria-hidden="true" />
);

interface FormFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ id, label, required, error, hint, children }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-medium text-gray-900">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-gray-600 text-xs">{hint}</p>}
    {error && (
      <p className="text-destructive text-xs flex items-center gap-1" role="alert">
        <AlertCircle className="h-3 w-3 flex-shrink-0" />
        {error}
      </p>
    )}
  </div>
);

// ── Props ───────────────────────────────────────────────────────────────────

export interface RegistrationFormSectionProps {
  register: UseFormRegister<RegistrationFormValues>;
  errors: FieldErrors<RegistrationFormValues>;
  fieldErrors: Record<string, string>;
  isSubmitting: boolean;
  submitError: string | null;
  onFormSubmit: React.FormEventHandler<HTMLFormElement>;
  accessCodeValidating: boolean;
  accessCodeResult: AccessCodeValidationResult | null;
  accessCodeError: string | null;
  accessCodeValue: string;
  handleAccessCodeBlur: () => void;
  onDismissError: () => void;
  programType: ProgramType;
}

// ── Main Component ──────────────────────────────────────────────────────────

export const RegistrationFormSection: React.FC<RegistrationFormSectionProps> = ({
  register, errors, fieldErrors, isSubmitting, submitError,
  onFormSubmit, accessCodeValidating, accessCodeResult, accessCodeError,
  accessCodeValue, handleAccessCodeBlur, onDismissError, programType,
}) => {
  const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);
  const isDemo = programType === PROGRAM_TYPE.DEMO;

  const inputBase =
    'w-full px-3 py-2.5 border rounded-md text-sm bg-background text-foreground ' +
    'placeholder:text-muted-foreground/60 transition-colors duration-200 hover:border-gray-400 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed';

  const inputCls = (hasErr: boolean, hasOk?: boolean) =>
    `${inputBase} ${hasErr ? 'border-destructive focus-visible:ring-destructive' : hasOk ? 'border-green-500 focus-visible:ring-green-500' : 'border-input hover:border-primary/50'}`;

  const fieldErr = (name: keyof RegistrationFormValues) =>
    errors[name]?.message || fieldErrors[name];
  const hasErr = (name: keyof RegistrationFormValues) =>
    !!errors[name] || !!fieldErrors[name];

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 shadow-sm p-6 sm:p-8">
      <h2 className="text-xl font-bold mb-1" style={{ color: ECGridColors.tealDark }}>
        {isDemo
          ? t('DemoRegistration_VAN_CTA', 'Register for ECGrid VAN Demo')
          : t('DeveloperRegistration_VAN_CTA', 'Register for ECGrid VAN Developer')}
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        {isDemo
          ? t('DemoRegistration_Instructions', 'Fill out the fields below and click continue. Your demo account will be set up immediately.')
          : t('DeveloperRegistration_Instructions', 'To sign up and get 30 days free, fill out the fields below then click continue.')}
      </p>

      {submitError && (
        <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg mb-6" role="alert" aria-live="polite">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="flex-1 text-sm">{submitError}</span>
          <button type="button" onClick={onDismissError} className="p-1 hover:bg-destructive/20 rounded transition-colors focus-visible:ring-2 focus-visible:ring-ring" aria-label="Dismiss error">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <form onSubmit={onFormSubmit} className="space-y-6">
        {/* Company */}
        <div className="space-y-4">
          <FormField id="companyName" label={t('DeveloperRegistration_CompanyName', 'Company Name')} required error={fieldErr('companyName')}>
            <input {...register('companyName')} id="companyName" type="text" autoComplete="organization"
              placeholder={t('DeveloperRegistration_CompanyName_Placeholder', 'Acme Corporation')}
              disabled={isSubmitting} className={inputCls(hasErr('companyName'))} />
          </FormField>
          <FormField id="companyAddress" label={t('DeveloperRegistration_CompanyAddress', 'Company Address')} required error={fieldErr('companyAddress')}>
            <textarea {...register('companyAddress')} id="companyAddress" autoComplete="street-address" rows={3}
              placeholder={t('DeveloperRegistration_CompanyAddress_Placeholder', '123 Business Ave, Suite 100\nCity, State 12345')}
              disabled={isSubmitting} className={`${inputCls(hasErr('companyAddress'))} resize-none`} />
          </FormField>
        </div>

        <div className="border-t border-gray-200" />

        {/* Contacts */}
        <div className="space-y-4">
          <FormField id="ownerContactName" label={t('DeveloperRegistration_OwnerContactName', 'Account Owner')} required error={fieldErr('ownerContactName')}>
            <input {...register('ownerContactName')} id="ownerContactName" type="text" autoComplete="name"
              placeholder={t('DeveloperRegistration_OwnerContactName_Placeholder', 'Full Name')}
              disabled={isSubmitting} className={inputCls(hasErr('ownerContactName'))} />
          </FormField>
          <FormField id="ownerContactEmail" label={t('DeveloperRegistration_OwnerContactEmail', 'Account Owner Email Address')} required error={fieldErr('ownerContactEmail')}>
            <input {...register('ownerContactEmail')} id="ownerContactEmail" type="email" autoComplete="email" spellCheck="false"
              placeholder={t('DeveloperRegistration_OwnerContactEmail_Placeholder', 'john@acme.com')}
              disabled={isSubmitting} className={inputCls(hasErr('ownerContactEmail'))} />
          </FormField>
          <FormField id="technicalContactName" label={t('DeveloperRegistration_TechnicalContactName', 'Technical Contact')} error={fieldErr('technicalContactName')}>
            <input {...register('technicalContactName')} id="technicalContactName" type="text" autoComplete="name"
              placeholder={t('DeveloperRegistration_TechnicalContactName_Placeholder', 'Full Name')}
              disabled={isSubmitting} className={inputCls(hasErr('technicalContactName'))} />
          </FormField>
          <FormField id="technicalContactEmail" label={t('DeveloperRegistration_TechnicalContactEmail', 'Technical Contact Email Address')} error={fieldErr('technicalContactEmail')}>
            <input {...register('technicalContactEmail')} id="technicalContactEmail" type="email" autoComplete="email" spellCheck="false"
              placeholder={t('DeveloperRegistration_TechnicalContactEmail_Placeholder', 'dev@acme.com')}
              disabled={isSubmitting} className={inputCls(hasErr('technicalContactEmail'))} />
          </FormField>
        </div>

        {/* Referral Code — developer only */}
        {!isDemo && (
          <>
            <div className="border-t border-gray-200" />
            <div className="space-y-4">
              <FormField id="accessCode" label={t('DeveloperRegistration_AccessCodeLabel', 'Referral Code')}
                error={accessCodeError ?? undefined}
                hint={t('DeveloperRegistration_AccessCodeHelp', 'Enter your code if you received one from a sales representative.')}>
                <div className="relative">
                  <input {...register('accessCode')} id="accessCode" type="text" autoComplete="off" spellCheck="false"
                    placeholder={t('DeveloperRegistration_AccessCode_Placeholder', 'ABC123')}
                    disabled={isSubmitting} onBlur={handleAccessCodeBlur}
                    className={`${inputCls(!!accessCodeError, accessCodeResult?.isValid)} pr-10`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    {accessCodeValidating && <LoadingSpinner className="h-4 w-4 text-muted-foreground" />}
                    {!accessCodeValidating && accessCodeResult?.isValid && <Check className="h-4 w-4 text-green-500" aria-hidden="true" />}
                    {!accessCodeValidating && accessCodeError && <AlertCircle className="h-4 w-4 text-destructive" aria-hidden="true" />}
                  </span>
                </div>
                {accessCodeResult?.isValid && accessCodeResult?.salespersonEmail && (
                  <p className="text-green-600 text-xs flex items-center gap-1 mt-1.5" role="status" aria-live="polite">
                    <Check className="h-3 w-3" />
                    {t('DeveloperRegistration_ValidCode_Referred', 'Referred by')} {accessCodeResult.salespersonEmail}
                  </p>
                )}
                {accessCodeResult?.isValid && !accessCodeResult?.salespersonEmail && accessCodeValue && (
                  <p className="text-green-600 text-xs flex items-center gap-1 mt-1.5" role="status" aria-live="polite">
                    <Check className="h-3 w-3" />
                    {t('DeveloperRegistration_ValidCode', 'Valid access code')}
                  </p>
                )}
              </FormField>
            </div>
          </>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          style={{ backgroundColor: isSubmitting ? '#9ca3af' : ECGridColors.blue }}
          onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = ECGridColors.tealDark; }}
          onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = ECGridColors.blue; }}
          className={`w-full py-3 px-4 text-white font-medium rounded-md transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {isSubmitting
            ? <span className="flex items-center justify-center gap-2"><LoadingSpinner className="h-5 w-5" />{t('DeveloperRegistration_Submitting', 'Processing…')}</span>
            : isDemo
              ? t('DemoRegistration_Submit', 'Create Demo Account')
              : t('DeveloperRegistration_Submit', 'Continue to Payment')}
        </button>
      </form>
    </div>
  );
};
